import { createIsomorphicFn } from "@tanstack/react-start";

import type {
  GlitchTipContextData,
  GlitchTipExceptionOptions,
  GlitchTipLogOptions,
  GlitchTipUser,
} from "./glitchtip-types";

type SetContextInput = {
  name: string;
  context: GlitchTipContextData;
};

type SetTagInput = {
  key: string;
  value: string | number | boolean | null | undefined;
};

type CaptureInput = {
  error: unknown;
  options?: GlitchTipExceptionOptions;
};

function isAbortError(error: unknown) {
  return error instanceof Error && error.name === "AbortError";
}

function isRedirectLikeError(error: unknown) {
  if (!error || typeof error !== "object") {
    return false;
  }

  const candidate = error as {
    isRedirect?: unknown;
    statusCode?: unknown;
    status?: unknown;
    href?: unknown;
  };

  return (
    candidate.isRedirect === true ||
    ((candidate.statusCode === 301 ||
      candidate.statusCode === 302 ||
      candidate.statusCode === 303 ||
      candidate.statusCode === 307 ||
      candidate.statusCode === 308 ||
      candidate.status === 301 ||
      candidate.status === 302 ||
      candidate.status === 303 ||
      candidate.status === 307 ||
      candidate.status === 308) &&
      typeof candidate.href === "string")
  );
}

function isIgnoredGlitchTipError(error: unknown) {
  return isAbortError(error) || isRedirectLikeError(error);
}

export const captureException = createIsomorphicFn()
  .client(async ({ error, options }: CaptureInput) => {
    if (isIgnoredGlitchTipError(error)) {
      return;
    }

    const { captureBrowserGlitchTipException } = await import("./glitchtip-browser");
    captureBrowserGlitchTipException(error, options);
  })
  .server(async ({ error, options }: CaptureInput) => {
    if (isIgnoredGlitchTipError(error)) {
      return;
    }

    const { captureServerGlitchTipException } = await import("./glitchtip-server");
    captureServerGlitchTipException(error, options);
  });

export const captureHandledError = createIsomorphicFn()
  .client(async ({ error, options }: CaptureInput) => {
    return captureException({
      error,
      options: {
        ...options,
        handled: true,
      },
    });
  })
  .server(async ({ error, options }: CaptureInput) => {
    return captureException({
      error,
      options: {
        ...options,
        handled: true,
      },
    });
  });

export const logInfo = createIsomorphicFn()
  .client(async (options: GlitchTipLogOptions) => {
    const { logBrowserGlitchTip } = await import("./glitchtip-browser");
    logBrowserGlitchTip("info", options);
  })
  .server(async (options: GlitchTipLogOptions) => {
    const { logServerGlitchTip } = await import("./glitchtip-server");
    logServerGlitchTip("info", options);
  });

export const logWarn = createIsomorphicFn()
  .client(async (options: GlitchTipLogOptions) => {
    const { logBrowserGlitchTip } = await import("./glitchtip-browser");
    logBrowserGlitchTip("warn", options);
  })
  .server(async (options: GlitchTipLogOptions) => {
    const { logServerGlitchTip } = await import("./glitchtip-server");
    logServerGlitchTip("warn", options);
  });

export const logError = createIsomorphicFn()
  .client(async (options: GlitchTipLogOptions) => {
    const { logBrowserGlitchTip } = await import("./glitchtip-browser");
    logBrowserGlitchTip("error", options);
  })
  .server(async (options: GlitchTipLogOptions) => {
    const { logServerGlitchTip } = await import("./glitchtip-server");
    logServerGlitchTip("error", options);
  });

export const setUser = createIsomorphicFn()
  .client(async (user: GlitchTipUser | null) => {
    const { setBrowserGlitchTipUser } = await import("./glitchtip-browser");
    setBrowserGlitchTipUser(user);
  })
  .server(async (user: GlitchTipUser | null) => {
    const { setServerGlitchTipUser } = await import("./glitchtip-server");
    setServerGlitchTipUser(user);
  });

export const clearUser = createIsomorphicFn()
  .client(async () => {
    const { clearBrowserGlitchTipUser } = await import("./glitchtip-browser");
    clearBrowserGlitchTipUser();
  })
  .server(async () => {
    const { clearServerGlitchTipUser } = await import("./glitchtip-server");
    clearServerGlitchTipUser();
  });

export const setTag = createIsomorphicFn()
  .client(async ({ key, value }: SetTagInput) => {
    const { setBrowserGlitchTipTag } = await import("./glitchtip-browser");
    setBrowserGlitchTipTag(key, value);
  })
  .server(async ({ key, value }: SetTagInput) => {
    const { setServerGlitchTipTag } = await import("./glitchtip-server");
    setServerGlitchTipTag(key, value);
  });

export const setContext = createIsomorphicFn()
  .client(async ({ name, context }: SetContextInput) => {
    const { setBrowserGlitchTipContext } = await import("./glitchtip-browser");
    setBrowserGlitchTipContext(name, context);
  })
  .server(async ({ name, context }: SetContextInput) => {
    const { setServerGlitchTipContext } = await import("./glitchtip-server");
    setServerGlitchTipContext(name, context);
  });

export type {
  GlitchTipContextData,
  GlitchTipExceptionOptions,
  GlitchTipLogOptions,
  GlitchTipTags,
  GlitchTipUser,
} from "./glitchtip-types";
